import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiArrowRight, FiCheckCircle, FiMail } from 'react-icons/fi';
import { DContext } from '../../context/DataContext';
import Logo from '../../assets/Logo bg.svg';
import { translateAuthServerMessage } from '../../utils/authServerMessage';
import MetaData from '../../utils/MetaData';

const ForgotPassword = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { BeURL, setLoader } = useContext(DContext);

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('email');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setErrors({});
  }, [i18n.language]);

  const handleSendOtp = async (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!email.trim()) {
      nextErrors.email = t('auth.validations.emailRequired');
    } else if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      nextErrors.email = t('auth.validations.emailInvalid');
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setLoader(true);
      const res = await fetch(`${BeURL}/forgot-password/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      const data = await res.json();
      setLoader(false);

      if (!data.success) {
        alert(translateAuthServerMessage(data.message, t, 'auth.messages.otpSendFailed'));
        return;
      }

      setStep('otp');
      alert(t('auth.messages.otpSent'));
    } catch (err) {
      setLoader(false);
      console.log('Error sending forgot-password OTP:', err);
      alert(t('auth.messages.serverError'));
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (!otp.trim() || !/^\d{6}$/.test(otp.trim())) {
      setErrors({ otp: t('auth.validations.otpInvalid') });
      return;
    }

    try {
      setLoader(true);
      const res = await fetch(`${BeURL}/forgot-password/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase(), otp: otp.trim() })
      });
      const data = await res.json();
      setLoader(false);

      if (!data.success || !data.resetToken) {
        alert(translateAuthServerMessage(data.message, t, 'auth.messages.otpVerifyFailed'));
        return;
      }

      navigate(`/reset-password?token=${encodeURIComponent(data.resetToken)}`, { replace: true });
    } catch (err) {
      setLoader(false);
      console.log('Error verifying forgot-password OTP:', err);
      alert(t('auth.messages.serverError'));
    }
  };

  return (
    <>
      <MetaData
        title='Forgot Password | Be Alright Krabi'
        description='Request a password reset OTP for your Be Alright Krabi account.'
        canonicalUrl={`${window.location.origin}/forgot-password`}
        robots='noindex,nofollow'
      />
      <div className="relative flex min-h-[85vh] flex-wrap items-center justify-center gap-6 px-4 py-10">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(23,57,189,0.12),_transparent_40%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.12),_transparent_45%)]" />

        <section className="relative w-full max-w-lg text-center">
          <img src={Logo} alt="logo" className="mx-auto h-28 w-auto sm:h-36" />
          <h1 className="mt-3 text-3xl font-black tracking-tight text-[#1739bd] sm:text-5xl">{t('auth.forgot.title')}</h1>
          <p className="mt-3 text-sm text-slate-600 sm:text-base">{t('auth.forgot.subtitle')}</p>
        </section>

        <section className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white/95 p-5 shadow-[0_18px_55px_rgba(15,23,42,0.12)] backdrop-blur sm:p-6">
          {step === 'email' ? (
            <form onSubmit={handleSendOtp} className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.email')}</span>
                <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                  <FiMail className="text-slate-500" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-transparent text-sm outline-none" placeholder="name@mail.com" />
                </div>
                {errors.email && <p className="mt-1 text-xs font-medium text-rose-600">{errors.email}</p>}
              </label>

              <button type="submit" className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1739bd] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
                <span>{t('auth.forgot.sendOtp')}</span>
                <FiArrowRight />
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-4">
              <p className="text-sm text-slate-600">{t('auth.forgot.otpSentTo', { email })}</p>

              <label className="block">
                <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">{t('auth.fields.otp')}</span>
                <div className="flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2.5 focus-within:border-[#1739bd]">
                  <FiCheckCircle className="text-slate-500" />
                  <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} className="w-full bg-transparent text-sm outline-none" placeholder="123456" />
                </div>
                {errors.otp && <p className="mt-1 text-xs font-medium text-rose-600">{errors.otp}</p>}
              </label>

              <div className="flex gap-2">
                <button type="button" onClick={() => setStep('email')} className="inline-flex w-1/2 items-center justify-center rounded-2xl border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100">
                  {t('auth.actions.back')}
                </button>
                <button type="submit" className="inline-flex w-1/2 items-center justify-center gap-2 rounded-2xl bg-[#1739bd] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:bg-indigo-700">
                  <span>{t('auth.forgot.verifyOtp')}</span>
                  <FiArrowRight />
                </button>
              </div>
            </form>
          )}

          <p className="mt-4 text-center text-sm text-slate-600">
            <Link to="/login" className="font-semibold text-[#1739bd] no-underline hover:text-indigo-700">{t('auth.forgot.backToLogin')}</Link>
          </p>
        </section>
      </div>
    </>
  );
};

export default ForgotPassword;
